import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import ImportErrorTable from '@core/components/ImportModal/ImportErrorTable';
import ImportModal from '@core/components/ImportModal/ImportModal';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { downloadFile } from '@core/util/downloadUtils';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import FuseUtils from '@fuse/utils';
import contasPagarApi from 'app/services/api/contasPagarApi';
import * as Actions from 'app/store/actions';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useState } from 'react';
import { FaArrowCircleDown } from 'react-icons/fa';
import { IoIosAlert } from 'react-icons/io';
import * as yup from 'yup';
import { ContasPagarAction } from '../../../store';
import { useContasPagar } from '../../../store/contasPagarContext';

const CONTA_PAGAR = 'Conta a pagar';

const ImportarContaPagarModal = () => {
  const [state, dispatch] = useContasPagar();
  const { abrirImportModal, arquivo } = state;
  const apiUtils = useApiUtils(dispatch, Actions);
  const [errorData, setErrorData] = useState([]);
  const [error, setError] = useState(false);
  const [idNotificacao, setIdNotificacao] = useState();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [progress, setProgress] = useState(0);

  const extensoesPermitidas = ['xlsx'];
  const titulo = error ? 'Erros identificados na importação' : 'Importar planilha de contas a pagar';

  useReduxLoading(loading);

  const obterModelo = async () => {
    try {
      setLoading(true);
      eventReactGA(CONTA_PAGAR, 'Obter planilha');

      const response = await contasPagarApi.obterModelo();
      downloadFile(response.data.bytes, response.data.nome);
    } catch (error) {
      apiUtils.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    ContasPagarAction.setArquivoImport(dispatch, '');
    setDisabled(false);
    if (error) {
      setError(false);
      setErrorData([]);
    } else {
      dispatch(ContasPagarAction.fecharImportModal());
      formik.resetForm(formik.initialValues);
    }
  };

  const handleUpload = (file) => {
    if (file) {
      try {
        const chaveNotificacao = FuseUtils.generateGUID();
        setIdNotificacao(chaveNotificacao);
        setDisabled(true);

        const arquivo = Object.assign(file, {
          id: undefined,
          nome: file.name,
          contentType: file.type,
        });

        const extensao = arquivo.nome.substring(arquivo.nome.lastIndexOf('.') + 1);

        if (!extensoesPermitidas.includes(extensao.toLowerCase())) {
          ContasPagarAction.setArquivoImport(dispatch, '');
          setDisabled(false);
          setLoading(false);
          dispatch(ContasPagarAction.erro('É permitido anexar somente arquivos no formato .xlsx', null));
        } else {
          const formData = new FormData();
          formData.append('arquivo', arquivo);

          setLoading(true);
          eventReactGA(CONTA_PAGAR, 'Importar planilha');
          contasPagarApi.importar(formData, chaveNotificacao);
        }
      } catch (error) {
        apiUtils.handleApiError(error);
      }
    }
  };

  const handleSubmit = () => {
    setLoading(true);
    handleUpload(arquivo);
  };

  const ImportarArquivoSchema = yup.object().shape({
    arquivo: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o arquivo')),
  });

  const getInitialValues = (registro) => ({
    ...registro,
    id: registro?.id,
    arquivo: registro?.arquivo,
  });

  const formik = useFormik({
    initialValues: getInitialValues(arquivo),
    validationSchema: ImportarArquivoSchema,
    onSubmit: handleSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const onFileChange = (event) => {
    const file = event.target.files[0];
    setProgress(0);

    formik.handleChange({
      target: {
        id: 'arquivo',
        value: file,
      },
    });

    ContasPagarAction.setArquivoImport(dispatch, file);
  };

  const handleProgressUpdate = (notificacao) => {
    if (idNotificacao === notificacao.id) {
      const mensagem = JSON.parse(notificacao.mensagem);
      const newProgress = parseFloat(mensagem.percentual);
      setProgress(newProgress);
      setLoading(false);

      if (newProgress === 100) {
        if (mensagem?.errors && mensagem?.errors?.length > 0) {
          if (mensagem.errors[0].campo === 'arquivo-error') {
            dispatch(ContasPagarAction.erro(mensagem.errors[0].mensagemErro, null));
            setDisabled(false);
            setProgress(0);
          } else if (mensagem.errors[0].campo === 'arquivo-planilha-error') {
            dispatch(ContasPagarAction.erro(mensagem.errors[0].mensagemErro, null));
            setDisabled(false);
            setProgress(0);
          } else {
            setError(true);
            setErrorData(mensagem.errors);
          }
        }
        if (mensagem?.errors?.length === 0) {
          dispatch(ContasPagarAction.sucesso('Importação realizada com sucesso.', null));
          handleClose();
          ContasPagarAction.carregarRegistros(dispatch, state.requestParams, state.filtroAvancadoFormatado);
        }
      }
    }
  };

  return (
    <SimpleDialog open={abrirImportModal} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <SimpleDialogContent
          header={{
            title: titulo,
            icon: error ? <IoIosAlert /> : <FaArrowCircleDown />,
            toolbar: (
              <>
                <CancelButton title="Cancelar" type="button" onClick={handleClose}>
                  {error ? 'Fechar' : 'Cancelar'}
                </CancelButton>
                {!error && (
                  <SaveButton type="submit" onClick={() => formik.handleSubmit(formik.values)} disabled={disabled}>
                    Processar
                  </SaveButton>
                )}
              </>
            ),
          }}
          content={
            <>
              {error ? (
                <ImportErrorTable errors={errorData} />
              ) : (
                <ImportModal
                  onFileChange={onFileChange}
                  arquivo={arquivo}
                  obterModelo={obterModelo}
                  progress={progress}
                  handleProgressUpdate={handleProgressUpdate}
                  formik={formik}
                />
              )}
            </>
          }
          contentStyle={{ padding: 0 }}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ImportarContaPagarModal;
